import React, { ReactElement } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dialog } from 'elmo-elements';
import './style.scss';
import {
  actions as ElearningPlayerActions,
  ElearningPlayerStateType,
} from '@/components/Player/modules/reducers/elearningPlayer';
import { QuizStateType } from '@/components/Player/modules/reducers/quiz';
import { ThemeBottomType } from '@/modules/types/theme';
import { frameNavSelector } from '@/modules/selectors/frame';
import FrameBottomView from './FrameBottomView';
import { ElearningDataType } from '@/modules/types/elearning';
import { FrameDataType } from '@/modules/types/frame';
import { MuteType } from '@/modules/types/elearning/muteType.enum';
import { resolveAssetUrl } from '@/lib/util';

export interface FrameBottomType {
  isPlayable: boolean;
  elearning: ElearningDataType | undefined;
  frames: FrameDataType[] | undefined;
  currentFrameId: string | undefined;
}

const FrameBottom = ({
  isMobileView,
}: {
  isMobileView?: boolean;
}): ReactElement => {
  const dispatch = useDispatch();

  const elearningPlayer: ElearningPlayerStateType = useSelector(
    (state: { elearningPlayer: ElearningPlayerStateType }) =>
      state.elearningPlayer,
  );

  const quiz: QuizStateType = useSelector(
    (state: { quiz: QuizStateType }) => state.quiz,
  );

  const frameNav = frameNavSelector({
    frameData: elearningPlayer.content.frames,
    currentFrameId: elearningPlayer.content.currentFrameId,
  });

  const handleNavigation = (nextFrameId: string) => {
    elearningPlayer.handlers.moveToFrame(nextFrameId);
    dispatch({
      type: ElearningPlayerActions.reset.type,
    });
  };

  const result: ThemeBottomType = {
    metadata: {},
  };

  const { elearning, frames, currentFrameId } = elearningPlayer.content;

  if (!elearning) return <div />;
  const currentFrame = frames
    ? currentFrameId
      ? frames.find((frameElement) => frameElement.id === currentFrameId)
      : frames[0]
    : {
        frameMetadata: {
          sound: '',
          mute: undefined,
        },
      };

  result.metadata = {
    isQuiz: elearningPlayer.isQuizReady,
    menu: elearning.theme?.menuMetadata || {},
    showPoweredBy: elearning.theme?.showPoweredBy || {},
    showHelpDesk: elearning.theme?.showHelpDesk || {},
    playerMetadata: elearning.playerMetadata,
    mute:
      currentFrame?.frameMetadata.mute ??
      elearning.playerMetadata.mute ??
      MuteType.HIDDEN,
    sound: '',
    currentFrame,
  };

  // set initial value of sound empty
  result.metadata.sound = '';
  if (currentFrame?.frameMetadata.sound) {
    result.metadata.sound = resolveAssetUrl({
      assetBasePath: elearning.assetBasePath,
      tenantId: elearning.tenantId,
      entity: 'elearnings',
      entityId: elearning.id,
      fileType: 'sounds',
      fileName: currentFrame.frameMetadata.sound,
    });
  }

  result.metadata.navigation = {
    exitQuiz: () => {
      // Check if user has passed the quiz
      const { quiz: quizData } = elearningPlayer.content;
      const score = quiz.result?.score || 0;
      const pass = quizData?.quizMetadata.pass || 0;
      const hasPassed = score >= pass;

      const manualClose = () => {
        Dialog.success({
          title: `Finished assessment`,
          content: `You can close the window manually now.`,
          okLabel: 'Okay',
        });
      };
      const closeWindow = () => {
        try {
          if (window.top?.close) {
            window.top?.close();
            setTimeout(() => {
              if (!window.top?.closed) {
                manualClose();
              }
            }, 100);
          } else {
            manualClose();
          }
        } catch (error) {
          manualClose();
        }
      };

      if (hasPassed) {
        // If user has passed, directly close the window
        closeWindow();
      } else {
        // If user hasn't passed, show the original warning and reload
        Dialog.confirm({
          withoutIcon: true,
          title: 'Warning!',
          content:
            'You will need to re-start from the beginning if you close your assessment and return to the module. Are you sure you want to close your assessment?',
          onOk() {
            closeWindow();
          },
          onCancel() {},
        });
      }
    },
    goPrevFrame: () => {
      // This is intentional
    },
    goNextFrame: () => {
      // This is intentional
    },
    total: frameNav.total,
    currentFramePage: frameNav.currentFramePage,
  };
  if (elearningPlayer.isPlayable) {
    result.metadata.navigation.goPrevFrame = () =>
      handleNavigation(frameNav.prevFrameId);
    result.metadata.navigation.goNextFrame = () =>
      handleNavigation(frameNav.nextFrameId);
  }
  return (
    <FrameBottomView
      isPlayable={elearningPlayer.isPlayable}
      bottom={result}
      isMobileView={isMobileView}
    />
  );
};

export default FrameBottom;
